import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { rolesApi } from '../../../api/roles.js';
import { permissionsApi } from '../../../api/permissions.js';
import { ApiError } from '../../../api/client.js';
import { applyServerValidation } from '../../../lib/forms.js';
import { t } from '../../../lib/i18n.js';

const EMPTY = { name: '', label: '', description: '' };

export function RoleFormModal({ open, onClose, initial }) {
  const editing = Boolean(initial && initial._id);
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState(new Set());

  const permsQuery = useQuery({
    queryKey: ['permissions', 'list'],
    enabled: open,
    queryFn: permissionsApi.list
  });

  const groupedPermissions = useMemo(() => {
    const list = (permsQuery.data && permsQuery.data.items) || [];
    const groups = new Map();
    for (const p of list) {
      const g = p.group || 'altro';
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g).push(p);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [permsQuery.data]);

  const { register, handleSubmit, reset, setError, formState: { errors } } = useForm({ defaultValues: EMPTY });

  useEffect(() => {
    if (!open) return;
    if (initial) {
      reset({ name: initial.name || '', label: initial.label || '', description: initial.description || '' });
      const perms = Array.isArray(initial.permissions)
        ? initial.permissions.map((p) => (typeof p === 'string' ? p : p.key))
        : [];
      setSelected(new Set(perms));
    } else {
      reset(EMPTY);
      setSelected(new Set());
    }
  }, [open, initial, reset]);

  const togglePerm = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleGroup = (groupKeys, allOn) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const k of groupKeys) {
        if (allOn) next.delete(k); else next.add(k);
      }
      return next;
    });
  };

  const mutation = useMutation({
    mutationFn: (values) => {
      const payload = { ...values, permissions: Array.from(selected) };
      for (const k of Object.keys(payload)) {
        if (payload[k] === '' || payload[k] === undefined) delete payload[k];
      }
      if (editing) {
        delete payload.name;
        return rolesApi.update(initial._id, payload);
      }
      return rolesApi.create(payload);
    },
    onSuccess: () => {
      toast.success(editing ? 'Ruolo aggiornato.' : 'Ruolo creato.');
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      onClose();
    },
    onError: (err) => {
      const applied = applyServerValidation(err, setError);
      if (!applied) toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title={editing ? `Modifica ruolo "${initial?.label}"` : 'Nuovo ruolo'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button form="role-form" type="submit" loading={mutation.isPending}>{t('common.save')}</Button>
        </>
      }
    >
      <form id="role-form" className="flex flex-col gap-4" onSubmit={handleSubmit(mutation.mutate)} noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Nome (slug)"
            hint="minuscole, cifre, - o _ (2-48 caratteri)"
            disabled={editing}
            error={errors.name?.message}
            {...register('name', editing ? {} : {
              required: 'Nome richiesto',
              pattern: { value: /^[a-z0-9_-]{2,48}$/, message: 'formato non valido' }
            })}
          />
          <Input label="Etichetta" error={errors.label?.message} {...register('label', { required: 'Etichetta richiesta' })} />
        </div>
        <Textarea label="Descrizione" rows={2} error={errors.description?.message} {...register('description')} />

        <div>
          <p className="mb-2 text-sm font-medium text-ink">Permessi assegnati</p>
          {permsQuery.isLoading ? (
            <p className="text-sm text-ink-muted">{t('common.loading')}</p>
          ) : (
            <div className="flex flex-col gap-3">
              {groupedPermissions.map(([group, perms]) => {
                const groupKeys = perms.map((p) => p.key);
                const allOn = groupKeys.every((k) => selected.has(k));
                return (
                  <div key={group} className="rounded-md border border-border bg-surface p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">{group}</p>
                      <button
                        type="button"
                        className="text-xs text-brand hover:text-brand-hover"
                        onClick={() => toggleGroup(groupKeys, allOn)}
                      >
                        {allOn ? 'Deseleziona' : 'Seleziona tutti'}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                      {perms.map((p) => (
                        <label key={p.key} className="flex items-start gap-2 text-xs text-ink">
                          <input
                            type="checkbox"
                            checked={selected.has(p.key)}
                            onChange={() => togglePerm(p.key)}
                            className="mt-0.5"
                          />
                          <span>
                            <code className="text-[11px] text-ink">{p.key}</code>
                            {p.description && <span className="block text-[11px] text-ink-muted">{p.description}</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}
